import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme
} from "@mui/material";
import MKTypography from "../components/MKTypography";
import { PRIMARY_PINK } from "../constants";

function AccessibilityPage() {
  const theme = useTheme();

  return (
    <Box sx={{ minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 3, md: 6 }, borderRadius: 2 }}>
          <MKTypography
            component="h1"
            variant="h3"
            fontWeight="bold"
            mb={3}
            sx={{ color: PRIMARY_PINK, textAlign: 'center' }}
          >
            Accessibility Statement
          </MKTypography>
          
          <MKTypography variant="body2" color="text.secondary" mb={4} sx={{ textAlign: 'center' }}>
            Last updated: {new Date().toLocaleDateString()}
          </MKTypography>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Our Commitment
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              San Diego Gaming is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all users.
            </MKTypography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Accessibility Standards
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines help make web content more accessible to people with disabilities and user-friendly for everyone.
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              Our accessibility efforts include:
            </MKTypography>
            <Box component="ul" sx={{ pl: 3 }}>
              <MKTypography component="li" variant="body1" mb={1}>
                Providing alternative text for all images
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Ensuring proper heading structure for screen readers
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Maintaining sufficient color contrast ratios
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Supporting keyboard navigation
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Providing descriptive link text and button labels
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Using semantic HTML elements
              </MKTypography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Accessibility Features
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              Our website includes the following accessibility features:
            </MKTypography>
            <Box component="ul" sx={{ pl: 3 }}>
              <MKTypography component="li" variant="body1" mb={1}>
                <strong>Screen Reader Compatibility:</strong> Our site is designed to work with screen readers and other assistive technologies
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                <strong>Keyboard Navigation:</strong> All interactive elements can be accessed using only a keyboard
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                <strong>High Contrast Mode:</strong> We provide both light and dark themes with sufficient contrast ratios
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                <strong>Responsive Design:</strong> Our site is optimized for all device sizes and screen resolutions
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                <strong>Focus Indicators:</strong> Clear visual indicators show which element has keyboard focus
              </MKTypography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Known Issues
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              We are aware of the following accessibility issues and are working to address them:
            </MKTypography>
            <Box component="ul" sx={{ pl: 3 }}>
              <MKTypography component="li" variant="body1" mb={1}>
                Some interactive elements may need additional ARIA labels for better screen reader support
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Video content may need captions for better accessibility
              </MKTypography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Feedback and Contact
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              We welcome your feedback on the accessibility of San Diego Gaming. Please let us know if you encounter accessibility barriers:
            </MKTypography>
                        <Box sx={{ pl: 2 }}>
              <MKTypography variant="body1" mb={1}>
                <strong>Email:</strong> accessibility@sandiegogaming.com
              </MKTypography>
            </Box>
            <MKTypography variant="body1" mb={2} sx={{ mt: 2 }}>
              We try to respond to feedback within 2 business days.
            </MKTypography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Assessment Approach
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              San Diego Gaming assessed the accessibility of this website through:
            </MKTypography>
            <Box component="ul" sx={{ pl: 3 }}>
              <MKTypography component="li" variant="body1" mb={1}>
                Self-evaluation using accessibility testing tools
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Manual testing with keyboard navigation
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                Screen reader testing
              </MKTypography>
              <MKTypography component="li" variant="body1" mb={1}>
                User feedback and testing
              </MKTypography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <MKTypography
              component="h2"
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{ color: PRIMARY_PINK }}
            >
              Updates
            </MKTypography>
            <MKTypography variant="body1" mb={2}>
              This accessibility statement is reviewed and updated regularly to reflect our ongoing commitment to accessibility. We will continue to improve our website's accessibility based on user feedback and evolving standards.
            </MKTypography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default AccessibilityPage;
